// MobileDrawer — 모바일 off-canvas 드로어(좌측 슬라이드 + 반투명 오버레이).
// 갓파일이 아님. A 배선: 모바일일 때 사이드바 nav를 이 안에 렌더(<MobileDrawer open onClose>{nav}</MobileDrawer>).
// 오버레이 탭 / Esc / (메뉴 선택 시 onClose 호출)로 닫힘.
// 🔴 덮개+패널은 `document.body` 에 포털로 그린다(f8b2a1) — 서랍이 어느 화면 셸 안에 있든 «쌓임 맥락» 이 서랍 밖의 것(푸시 안내 띠 z 990 등)과 맞붙게 한다.
//    임베드 화면(메신저·드라이브)은 셸이 EmbedHost 슬롯(`position:fixed; z-index:25` — fixed 는 z-index 와 무관하게 쌓임 맥락을 만든다) 안에 있어,
//    서랍이 제자리(셸 안)에 그려지면 z-index 1101 이어도 그 맥락(25) 안에 갇혀 푸시 안내 띠(990)에 가려졌다.
//    React 이벤트·컨텍스트(라우터 등)는 포털을 건너 그대로 이어진다 · 서랍 모드 알림 창(AppSidebar)도 이미 body 포털이다.
// 🔴 Tab 은 패널 안에서 돈다(g2785) — 패널은 body 에 붙어 있어 두 끝에서 Tab 하면 (뒤 포털 또는 브라우저 UI 를 거쳐) 뒤 화면(`#root`)으로 초점이 샌다.
//    `#root` 에 inert 를 쓰지 않는 이유 = 확인창(AppDialogHost)·녹음 알약 같은 맨 위 층이 `#root` 안이라 함께 갇힌다. 그래서 패널의 React onKeyDown 으로만 돌린다.
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// NotificationCenter.jsx 의 FOCUSABLE 과 같은 문자열(공용 훅이 없어 이 파일 상수로 둔다).
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function MobileDrawer({ open, onClose, children, width = 284 }) {
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    // 🔴 Escape 한 번 = 가장 위 층 하나만. 이 리스너는 document «버블»이라 안쪽 창(통합 검색·알림 창 — window «버블»)보다
    //    항상 먼저 돈다. 그 시점엔 안쪽 창이 아직 열려 있어 DOM 에 `data-gw-escape-layer` 표식이 있다 →
    //    서랍은 비켜서고 → 안쪽 창이 자기 리스너로 닫힌다 → 다음 Escape 에 서랍이 닫힌다.
    //    Escape 의 preventDefault: 알림 창은 하지 않는다 — 일부러다(다른 화면의 defaultPrevented 검사 처리기·input[type=search] 지우기를 막지 않으려고).
    //    검색 창은 27af140c 그대로 한다. 두 창은 preventDefault 로 서랍에 알리지 않는다(검색 창의 preventDefault 는 서랍 리스너보다 늦다) — 서랍은 표식(`data-gw-escape-layer`)을 보고 비켜선다.
    //    그 밖에 이미 처리된(defaultPrevented) Escape 도 무시한다.
    const onKey = (e) => {
      if (e.key !== 'Escape' || !onClose) return;
      if (e.defaultPrevented || document.querySelector('[data-gw-escape-layer]')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  // Tab 가두기 — 기존 관용구(NotificationCenter.trapTab 등)와 같은 꼴: 두 끝에서만 반대 끝으로 돌리고 그 밖은 브라우저 기본 이동에 맡긴다.
  // 핸들러가 패널의 React onKeyDown 이라 `#root` 안 맨 위 층(확인창·녹음 알약)에 초점이 있을 때의 Tab 은 여기로 오지 않는다.
  // 패널 자신도 tabIndex={-1} 이라 패널 빈 곳을 눌러 초점이 패널에 있을 때도 Tab 이 여기로 온다(→ 첫/마지막 요소).
  const trapTab = (e) => {
    if (e.key !== 'Tab' || e.defaultPrevented || !open) return;
    const root = panelRef.current;
    // 🔴 React 이벤트는 포털을 건너 React 트리로 거품이 오른다 — 서랍 안 aside 가 연 알림 창·통합 검색 창(body 포털 · 자기 trapTab 있음)의 Tab 이 여기까지 올라온다.
    //    DOM 으로 패널 안에서 난 키만 다룬다.
    if (!root || !root.contains(e.target)) return;
    const all = Array.from(root.querySelectorAll(FOCUSABLE));
    // 배치 상자가 없는 요소가 «마지막» 이 되면 그 앞에서 Tab 이 패널 밖으로 샌다 → 상자가 있는 것만 남긴다.
    // 단 거른 결과가 비면(배치가 없는 환경 = jsdom 은 전부 0) 거르지 않은 목록을 쓴다.
    const shown = all.filter((el) => el.getClientRects().length > 0);
    const nodes = shown.length ? shown : all;
    if (!nodes.length) { e.preventDefault(); return; }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const at = document.activeElement;
    if (e.shiftKey && (at === first || at === root)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (at === last || at === root)) { e.preventDefault(); first.focus(); }
  };

  const layer = (
    <>
      {/* 반투명 오버레이 — 탭하면 닫힘 */}
      <div onClick={onClose} aria-hidden="true"
        style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--gw-overlay, #19191945)',
          opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none', transition: 'opacity .2s ease' }} />
      {/* 슬라이드 패널 */}
      <div ref={panelRef} onKeyDown={trapTab} tabIndex={-1} role="dialog" aria-modal="true" aria-label="전체 메뉴" aria-hidden={!open}
        style={{ outline: 'none', position: 'fixed', top: 0, bottom: 0, left: 0, width, maxWidth: 'min(88vw, 320px)', height: 'var(--gw-vv-h, var(--gw-app-h, 100dvh))', zIndex: 1101,
          background: 'var(--gw-sidebar, #f7f7f5)',   // 그림자 없음 — 시안 F ≤900 `.gateway-sidebar`(:27)는 위치·폭·transform 만 바꾸고 box-shadow 가 없다(덮개 --overlay 가 경계를 만든다) · f8b2
          transform: open ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform .25s ease, visibility .25s ease',
          visibility: open ? 'visible' : 'hidden', pointerEvents: open ? 'auto' : 'none',
          overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', boxSizing: 'border-box' }}>
        {children}
      </div>
    </>
  );
  return typeof document === 'undefined' ? layer : createPortal(layer, document.body);
}
